/**
 * @file TransfersFilters.tsx
 * @description Строка фильтров реестра перемещений (phase19.23 этап B;
 * phase19.31 зона «Склад» — пилюли кабинета вместо самодельных).
 *
 * Состав: поиск по номеру → статус пилюлями и пилюля-переключатель
 * «Быстрые действия» → период и склад-источник справа.
 *
 * «Быстрые действия» ДОБАВЛЯЮТ быстрые перемещения к обычным документам, а
 * не переключают выдачу: «Положить товар» и «Переставить» — те же
 * перемещения, и кладовщику нужен общий поток, когда он ищет, куда уехала
 * партия. Без пилюли параметр `source` не шлём вовсе — сервер сам прячет
 * быстрые действия; с пилюлей шлём все виды из реестра
 * `TRANSFER_SOURCE_LABEL`, чтобы новый вид документа появился здесь сам.
 */

import { FilterBar } from "../../ui/FilterBar";
import { FilterChips, FilterToggleChips } from "../../ui/FilterChips";
import { SearchInput } from "../../ui/SearchInput";
import { SelectField } from "../../ui/SelectField";
import { TRANSFER_SOURCE_LABEL, type TransferSource } from "../../../lib/api-transfers";
import type { WarehouseDto } from "../../../lib/api-wms";
import { WMS_PERIOD_OPTIONS } from "../../../lib/wms-period";
import { warehouseOptions } from "../../../lib/wms-registry-filters";

/** Статусы перемещения — пилюлями; новый статус = запись. */
const STATUS_CHIPS: { value: "" | "DRAFT" | "COMMITTED" | "CANCELLED"; label: string }[] = [
  { value: "", label: "Все" },
  { value: "DRAFT", label: "Черновики" },
  { value: "COMMITTED", label: "Проведённые" },
  { value: "CANCELLED", label: "Отменённые" },
];

/** Все виды документа: пилюля «Быстрые действия» снимает серверное скрытие. */
const ALL_SOURCES = Object.keys(TRANSFER_SOURCE_LABEL) as TransferSource[];

/** Значение фильтра `source` при включённой пилюле. */
const ALL_SOURCES_VALUE = ALL_SOURCES.join(",");

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  /** Пусто — быстрые действия скрыты сервером. */
  source: string;
  onSourceChange: (value: string) => void;
  period: string;
  onPeriodChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  warehouseId: string;
  onWarehouseChange: (value: string) => void;
  warehouses: WarehouseDto[];
}

export function TransfersFilters({
  search,
  onSearchChange,
  source,
  onSourceChange,
  period,
  onPeriodChange,
  status,
  onStatusChange,
  warehouseId,
  onWarehouseChange,
  warehouses,
}: Props) {
  const quickShown = source !== "";

  return (
    <FilterBar
      search={
        <SearchInput value={search} onChange={onSearchChange} placeholder="Номер документа" ariaLabel="Поиск по перемещениям" />
      }
      // Телефон: статус и «Быстрые действия» — в шторке «Фильтры · N»
      // (единое решение склада, phase19.31): на 360 пилюли не помещались.
      chipsCollapse={{
        activeCount: (status ? 1 : 0) + (quickShown ? 1 : 0),
        onReset: () => {
          onStatusChange("");
          onSourceChange("");
        },
      }}
      chips={
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <FilterChips
            aria-label="Статус перемещения"
            items={STATUS_CHIPS}
            value={status as (typeof STATUS_CHIPS)[number]["value"]}
            onChange={onStatusChange}
          />
          <FilterToggleChips
            aria-label="Вид перемещений"
            items={[
              {
                key: "quick",
                label: "Быстрые действия",
                pressed: quickShown,
                onToggle: () => onSourceChange(quickShown ? "" : ALL_SOURCES_VALUE),
              },
            ]}
          />
        </div>
      }
    >
      <SelectField
        value={period}
        onChange={onPeriodChange}
        options={WMS_PERIOD_OPTIONS}
        ariaLabel="Период"
        className="w-full sm:w-[160px]"
      />
      <SelectField
        value={warehouseId}
        onChange={onWarehouseChange}
        options={warehouseOptions(warehouses)}
        placeholder="Все склады"
        ariaLabel="Склад-источник"
        className="w-full sm:w-[200px]"
      />
    </FilterBar>
  );
}
